/**
 * <license header>
 */

import { State } from '@adobe/aio-sdk';

import AdobeAuth from '../../commerce/adobe-auth';
import BearerToken from '../../integration/bearer-token';
import CustomLogger from '../custom-logger';
import { ImsTokenResult } from './types';

/**
 * ImsToken for Adobe IMS Token Management
 *
 * This class handles Adobe IMS (Identity Management System) token operations
 * for authenticating API requests to Adobe services.
 *
 * @example
 * ```typescript
 * const imsToken = new ImsToken(
 *   'client-id',
 *   'client-secret',
 *   'tech-account-id',
 *   'tech@example.com',
 *   'org-id',
 *   ['openid', 'AdobeID'],
 *   logger, // optional
 *   'my_custom_cache_key', // optional
 *   'my-custom-context' // optional
 * );
 * const token = await imsToken.execute();
 * ```
 */
class ImsToken {
  /** OAuth client ID for Adobe IMS authentication */
  private readonly clientId: string;

  /** OAuth client secret for Adobe IMS authentication */
  private readonly clientSecret: string;

  /** Technical account ID for service-to-service authentication */
  private readonly technicalAccountId: string;

  /** Technical account email for service-to-service authentication */
  private readonly technicalAccountEmail: string;

  /** IMS organization ID */
  private readonly imsOrgId: string;

  /** Array of scopes required for the token */
  private readonly scopes: Array<string>;

  /** Custom logger instance for logging operations */
  private readonly customLogger: CustomLogger;

  /** State property for managing token state */
  private state: any | undefined = undefined;

  /** Optional token context for additional authentication parameters */
  private readonly tokenContext: string;

  /** Key for storing token in state (State API only available in Adobe I/O Runtime) */
  private readonly key: string;

  /**
   * Creates an instance of ImsToken
   *
   * @param clientId - OAuth client ID for Adobe IMS authentication
   * @param clientSecret - OAuth client secret for Adobe IMS authentication
   * @param technicalAccountId - Technical account ID for service-to-service authentication
   * @param technicalAccountEmail - Technical account email for service-to-service authentication
   * @param imsOrgId - IMS organization ID
   * @param scopes - Array of scopes required for the token
   * @param logger - Optional logger instance for logging operations
   * @param cacheKey - Optional custom cache key for token storage (defaults to 'runtime_api_gateway_token')
   * @param tokenContext - Optional token context for authentication (defaults to 'runtime-api-gateway-context')
   */
  constructor(
    clientId: string,
    clientSecret: string,
    technicalAccountId: string,
    technicalAccountEmail: string,
    imsOrgId: string,
    scopes: Array<string>,
    logger: any = null,
    cacheKey?: string,
    tokenContext?: string
  ) {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.technicalAccountId = technicalAccountId;
    this.technicalAccountEmail = technicalAccountEmail;
    this.imsOrgId = imsOrgId;
    this.scopes = scopes;
    this.customLogger = new CustomLogger(logger);
    this.key = cacheKey || 'ims_token';
    this.tokenContext = tokenContext || 'ims-context';
  }

  /**
   * Executes IMS token generation or retrieves a cached token
   *
   * This method first checks for a cached token. If a valid cached token exists,
   * it returns that. Otherwise, it generates a new token, caches it, and returns it.
   *
   * @returns A promise that resolves to the IMS token string or null if generation fails
   * @example
   * ```typescript
   * const token = await imsToken.execute();
   * if (token) {
   *   console.log('Token obtained:', token);
   * }
   * ```
   */
  async execute(): Promise<string | null> {
    try {
      this.customLogger.info('Starting IMS token generation/retrieval process');

      const currentValue = await this.getValue();
      if (currentValue !== null) {
        this.customLogger.info('Found cached IMS token, returning cached value');
        return currentValue;
      }

      this.customLogger.info('No cached token found, generating new IMS token');

      let result: ImsTokenResult = {
        token: null,
        expire_in: 86399, // Default fallback, will be overridden by actual token expiry
      };

      const response = await this.getImsToken();
      if (response !== null) {
        result = response;
      }

      if (result.token !== null) {
        this.customLogger.info(`Generated new IMS token, caching for ${result.expire_in} seconds`);
        await this.setValue(result);
      }

      return result.token;
    } catch (error: any) {
      this.customLogger.error(`Failed to execute IMS token generation: ${error.message}`);
      return null;
    }
  }

  /**
   * Generates a new IMS token from Adobe IMS service
   *
   * @returns A promise that resolves to ImsTokenResult or null if generation fails
   * @private
   */
  async getImsToken(): Promise<ImsTokenResult | null> {
    try {
      this.customLogger.debug(`Calling AdobeAuth.getToken with context: ${this.tokenContext}`);

      const token = await AdobeAuth.getToken(
        this.clientId,
        this.clientSecret,
        this.technicalAccountId,
        this.technicalAccountEmail,
        this.imsOrgId,
        this.scopes,
        this.tokenContext
      );

      if (token !== null && token !== undefined) {
        this.customLogger.debug('Received token from AdobeAuth, parsing with BearerToken.info');

        // Use BearerToken.info to extract actual expiration from JWT token
        const tokenInfo = BearerToken.info(token);

        if (!tokenInfo.isValid) {
          this.customLogger.error('Received invalid or expired token from IMS');
          return null;
        }

        // Calculate expire_in from timeUntilExpiry (convert milliseconds to seconds)
        const expireInSeconds = tokenInfo.timeUntilExpiry
          ? Math.floor(tokenInfo.timeUntilExpiry / 1000)
          : 86399; // Fallback to 24 hours if unable to determine

        this.customLogger.debug(`Token expires in ${expireInSeconds} seconds`);

        return {
          token: token,
          expire_in: expireInSeconds,
        };
      }

      this.customLogger.error('Received null or undefined token from IMS');
      return null;
    } catch (error: any) {
      this.customLogger.error(`Failed to get IMS token: ${error.message}`);
      return null;
    }
  }

  /**
   * Caches the IMS token in the state store with TTL
   *
   * @param result - The token result containing the token and expiration time
   * @returns A promise that resolves to true if caching succeeded, false otherwise
   * @private
   */
  async setValue(result: ImsTokenResult): Promise<boolean> {
    try {
      const state = await this.getState();
      if (state === null) {
        this.customLogger.info('State API not available, skipping token caching');
        // State API not available, skip caching
        return true; // Return true since token generation succeeded
      }

      // Apply 10-minute buffer (600 seconds) to prevent token expiry during usage
      const ttlWithBuffer = Math.max(result.expire_in - 600, 3600); // Minimum 60 minutes TTL

      this.customLogger.debug(
        `Caching IMS token with TTL: ${ttlWithBuffer} seconds (original: ${result.expire_in})`
      );

      await state.put(this.key, result.token, { ttl: ttlWithBuffer });
      return true;
    } catch (error: any) {
      this.customLogger.error(`Failed to cache IMS token: ${error.message}`);
      return true; // Return true since token generation succeeded
    }
  }

  /**
   * Retrieves a cached IMS token from the state store
   *
   * @returns A promise that resolves to the cached token string or null if not found
   * @private
   */
  async getValue(): Promise<string | null> {
    try {
      this.customLogger.debug('Checking for cached IMS token');

      const state = await this.getState();
      if (state === null) {
        this.customLogger.debug('State API not available, cannot retrieve cached token');
        // State API not available, skip caching
        return null;
      }

      const value = await state.get(this.key);
      if (value !== undefined && value.value) {
        this.customLogger.debug('Found cached IMS token');
        return value.value;
      }

      this.customLogger.debug('No cached IMS token found');
    } catch (error: any) {
      this.customLogger.error(`Failed to retrieve cached IMS token: ${error.message}`);
    }

    return null;
  }

  /**
   * Initializes and returns the state store instance
   *
   * @returns A promise that resolves to the state instance or null if initialization fails
   * @private
   */
  async getState(): Promise<any> {
    if (this.state === undefined) {
      try {
        this.customLogger.debug('Initializing State API for token caching');
        this.state = await State.init();
      } catch (error: any) {
        this.customLogger.error(`Failed to initialize State API: ${error.message}`);
        this.state = null;
      }
    }
    return this.state;
  }
}

export default ImsToken;
